import ActionButton from "@/components/buttons/ActionButton";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useAssignFinancialMediation } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { FinancialMediationStatus } from "../../enums";
import { AssignFinancialMediation, FinancialMediation } from "../../types";

interface Props {
  open: boolean;
  financialMediation: FinancialMediation;
  onFormCancel: VoidFunction;
}

const FinancialMediationAssignForm = ({ open, financialMediation, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<AssignFinancialMediation>();

  const assignFinancialMediation = useAssignFinancialMediation({ globalSpinner: false });

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) =>
        assignFinancialMediation.mutate(
          {
            id: financialMediation.id,
            object: {
              optimisticLockVersion: financialMediation.optimisticLockVersion,
              status: FinancialMediationStatus.ASSIGNED_TO_CONTRACT,
              contractNumbers: values.contractNumbers,
            },
          },
          {
            onError: (error) => setErrorsToForm(form, "financialMediation.attrs.assign", error.violations),
            onSuccess: onFormCancel,
          },
        ),
      )
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t("financialMediation.titles.assign")}
      cancelText={t("common.cancel")}
      okText={t("common.assign")}
      confirmLoading={assignFinancialMediation.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="financialMediationAssignForm">
        <Form.List name="contractNumbers" initialValue={[""]}>
          {(fields, { add, remove }) => (
            <>
              <Row gutter={rowGutter}>
                {fields.map((field) => (
                  <Col span={6} key={field.key}>
                    <Form.Item
                      name={[field.name]}
                      label={t("financialMediation.helpers.contractNumberOrder", { number: field.name + 1 })}
                      rules={[
                        validations.notBlank,
                        validations.size(1, 64),
                        validations.noRepeatedValue("contractNumbers"),
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                ))}
              </Row>

              <Space className="margin-bottom-medium">
                <ActionButton icon="plus" disabled={fields.length >= 8} onClick={() => add()}>
                  {t("common.add")}
                </ActionButton>

                <ActionButton icon="minus" disabled={fields.length <= 1} onClick={() => remove(fields.length - 1)}>
                  {t("common.remove")}
                </ActionButton>
              </Space>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default FinancialMediationAssignForm;
